import { useEffect, useState } from 'react';

import * as S from './Tabs.styles';
import { TabType } from './types';

type TabsProps = {
  tabs: TabType[];
  /**
   * Active tab index. Pass it together with `onSelect` to drive the tabs from
   * outside (e.g. from a URL query param); omit both to keep the internal
   * state, which is what every existing caller does.
   */
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  /**
   * Keep a panel mounted (hidden) once it has been opened, so switching back
   * doesn't refetch or lose its state. Off by default: panels mount only while
   * selected, as before.
   */
  keepVisitedMounted?: boolean;
};

const defaultProps: Partial<TabsProps> = {};

export function Tabs(props: TabsProps): React.ReactElement {
  const { tabs, selectedIndex, onSelect, keepVisitedMounted } = { ...defaultProps, ...props };

  const [ownIndex, ownIndexSet] = useState(0);
  // Controlled only when the caller supplies both halves; otherwise the
  // component keeps owning the state, exactly as before.
  const isControlled = selectedIndex !== undefined && !!onSelect;
  const tabIndex = isControlled ? selectedIndex : ownIndex;

  const [visited, visitedSet] = useState<number[]>([tabIndex]);
  useEffect(() => {
    visitedSet((prev) => (prev.includes(tabIndex) ? prev : [...prev, tabIndex]));
  }, [tabIndex]);

  const titlesMapped = tabs?.map((tab: TabType) => <S.Tab key={tab.id}>{tab.title}</S.Tab>);

  // `selected` comes from the tab's POSITION, not its id — ids are React keys
  // and need not be 1-based. Callers use the flag to skip mounting an inactive
  // panel's content (charts must not mount inside a display:none container).
  const panelsMapped = tabs?.map((tab: TabType, index: number) => (
    <S.Panel key={tab.id} forceRender={keepVisitedMounted && visited.includes(index)}>
      {tab.content(index === tabIndex)}
    </S.Panel>
  ));

  return (
    <S.Tabs
      selectedIndex={tabIndex}
      onSelect={(index) => (isControlled ? onSelect(index) : ownIndexSet(index))}
    >
      <S.List>{titlesMapped}</S.List>

      {panelsMapped}
    </S.Tabs>
  );
}
